/**
 * The credit curve: how much credit a single dimension of a note earns for how far off it was.
 *
 * The horizontal axis is the signed deviation (early / late, soft / loud, short / long); the
 * vertical axis is credit from 1 down to 0. A full-credit plateau sits anywhere on the axis and
 * each side has its own arm, so early and late can be different mistakes. An arm is a list of
 * breakpoints walking outward from the plateau edge; each segment between breakpoints is a
 * step (a cliff just past the breakpoint), a straight line, or an eased curve. Past the last
 * breakpoint the arm stays flat at that breakpoint's credit, so an arm with no breakpoints
 * means "do not care" on that side.
 *
 * Credit never rises as the deviation grows. {@link validateCreditCurve} enforces that.
 *
 * Each side (its half of the plateau and its arm) may take its own unit, and a timing side may
 * carry a floor and a cap in beats or ms on its plateau edge, so a window can be "half the note
 * early, but never more than 0.75 beat" and "a fixed 0.3 beat late". A curve with
 * one unit needs none of that and evaluates exactly as before. A curve with per-side settings is
 * resolved for each note ({@link resolveCurve}) into a single-unit curve before it is read.
 */

import { GradingConfigError } from './types';

/**
 * - `ms`: absolute milliseconds.
 * - `beats`: tempo-relative, in beats at the local tempo.
 * - `proportion`: relative to the note's notated length (offset and duration).
 * - `velocity`: MIDI velocity units.
 * - `semitones`: pitch distance.
 * - `fingers`: 0 for the notated finger, 1 for any other.
 */
export type CurveUnit = 'ms' | 'beats' | 'proportion' | 'velocity' | 'semitones' | 'fingers';

export type SegmentShape = 'step' | 'linear' | 'curve';

export interface CurvePoint {
  /** Distance beyond the plateau edge, in the curve's unit. Never decreases along an arm. */
  readonly distance: number;
  /** Credit at this breakpoint. Never above the previous breakpoint's credit. */
  readonly credit: number;
  /** How credit travels from the previous breakpoint to this one. */
  readonly shape: SegmentShape;
  /** For `curve` segments: the easing exponent (1 is a straight line, above 1 falls late). */
  readonly exponent?: number;
}

export interface CreditCurve {
  /** The unit of both sides, unless a side declares its own in {@link CreditCurve.sides}. */
  readonly unit: CurveUnit;
  /** The full-credit span on the signed deviation axis, `[low, high]` with low <= high. */
  readonly plateau: readonly [number, number];
  /** Arm for deviations below the plateau (early, soft, short). */
  readonly below: readonly CurvePoint[];
  /** Arm for deviations above the plateau (late, loud, long). */
  readonly above: readonly CurvePoint[];
  /**
   * Per-side settings: `below` covers `plateau[0]` and the below arm, `above` covers
   * `plateau[1]` and the above arm. A side left out takes `unit` and no limits.
   */
  readonly sides?: CurveSides;
}

/** Units a floor or cap is written in. */
export type LimitUnit = 'beats' | 'ms';

/** A fixed amount of time: the floor or cap on a side's distances from the target. */
export interface CurveLimit {
  readonly amount: number;
  readonly unit: LimitUnit;
}

/** A point of a {@link LengthCurve}: at this notated length (beats), this value (beats). */
export interface LengthPoint {
  readonly length: number;
  readonly value: number;
  /** How the value travels from the previous point to this one. */
  readonly shape: SegmentShape;
  /** For `curve` segments: the easing exponent. */
  readonly exponent?: number;
}

/**
 * A plateau edge that moves with the note's notated length, in beats. With `from: 'target'` a
 * value is the edge's distance from the target; with `from: 'start'` it is the time from the
 * note's start (how long the key must be held, for a release), so the distance from the target is
 * the length minus it. Between points the value travels by the segment's shape; below the first
 * point and past the last, the edge keeps that point's distance from the target. A `from: 'start'`
 * hold longer than the note gives a negative distance; the edge is then put on the target.
 */
export interface LengthCurve {
  readonly from: 'target' | 'start';
  readonly points: readonly LengthPoint[];
}

/** One side's own settings. */
export interface CurveSide {
  readonly unit: CurveUnit;
  /**
   * Floor on this side's plateau edge (its distance from the target). The breakpoints beyond it
   * keep their own widths, so a yellow band survives a binding floor or cap. When a floor and a
   * cap in different units cross (a beats floor above an ms cap at some tempos), the cap wins.
   */
  readonly min?: CurveLimit;
  /** Cap on this side's plateau edge (its distance from the target). */
  readonly max?: CurveLimit;
  /** Moves this side's plateau edge outward by a distance that depends on the notated length. */
  readonly edgeByLength?: LengthCurve;
}

export interface CurveSides {
  readonly below?: CurveSide;
  readonly above?: CurveSide;
}

/** What a note gives a curve to resolve against. Null where the note cannot give it. */
export interface NoteScale {
  /** Milliseconds per beat at the note. */
  readonly msPerBeat: number | null;
  /** The note's notated length in milliseconds. */
  readonly lengthMs: number | null;
  /** The note's notated length in beats. */
  readonly lengthBeats: number | null;
}

/** The units a side may convert into ms: the only ones a curve with per-side settings may use. */
export const TIME_UNITS: readonly CurveUnit[] = ['ms', 'beats', 'proportion'];

const ARM_START: CurvePoint = { distance: 0, credit: 1, shape: 'step' };

function segmentCredit(from: CurvePoint, to: CurvePoint, distance: number): number {
  if (to.shape === 'step') {
    return from.credit;
  }
  const span = to.distance - from.distance;
  const progress = span > 0 ? (distance - from.distance) / span : 1;
  const eased = to.shape === 'curve' ? Math.pow(progress, to.exponent ?? 1) : progress;
  return from.credit + (to.credit - from.credit) * eased;
}

/** Credit on one arm at a distance (>= 0) beyond the plateau edge. */
function armCredit(arm: readonly CurvePoint[], distance: number): number {
  const result = arm.reduce<{ credit: number | null; previous: CurvePoint }>(
    (state, point) => {
      if (state.credit !== null) {
        return state;
      }
      if (distance <= point.distance) {
        return { credit: segmentCredit(state.previous, point, distance), previous: point };
      }
      return { credit: null, previous: point };
    },
    { credit: null, previous: ARM_START },
  );
  return result.credit === null ? result.previous.credit : result.credit;
}

/**
 * The credit a signed deviation earns on the curve. A curve with per-side settings must be
 * resolved for the note first ({@link resolveCurve}).
 */
export function creditAt(curve: CreditCurve, deviation: number): number {
  if (curve.sides !== undefined) {
    throw new GradingConfigError('a curve with per-side settings must be resolved for its note');
  }
  const [low, high] = curve.plateau;
  if (deviation >= low && deviation <= high) {
    return 1;
  }
  return deviation < low
    ? armCredit(curve.below, low - deviation)
    : armCredit(curve.above, deviation - high);
}

function validateArm(arm: readonly CurvePoint[], side: string): string[] {
  return arm.reduce<{ errors: string[]; previous: CurvePoint }>(
    (state, point, index) => {
      const where = `${side} arm point ${index}`;
      const errors = [...state.errors];
      if (!Number.isFinite(point.distance) || point.distance < 0) {
        errors.push(`${where}: distance must be a finite number >= 0`);
      }
      if (point.distance < state.previous.distance) {
        errors.push(`${where}: distances must not decrease along an arm`);
      }
      if (!(point.credit >= 0 && point.credit <= 1)) {
        errors.push(`${where}: credit must be between 0 and 1`);
      }
      if (point.credit > state.previous.credit) {
        errors.push(`${where}: credit rises as the deviation grows (curves must be monotonic)`);
      }
      if (point.shape === 'curve' && !(typeof point.exponent === 'number' && point.exponent > 0)) {
        errors.push(`${where}: a curve segment needs a positive exponent`);
      }
      return { errors, previous: point };
    },
    { errors: [], previous: ARM_START },
  ).errors;
}

function limitErrors(limit: CurveLimit | undefined, where: string): string[] {
  if (limit === undefined) {
    return [];
  }
  return Number.isFinite(limit.amount) && limit.amount >= 0
    ? []
    : [`${where}: a limit's amount must be a finite number >= 0`];
}

function lengthCurveErrors(lengthCurve: LengthCurve, where: string): string[] {
  const pointErrors = lengthCurve.points.reduce<{ errors: string[]; previous: number }>(
    (state, point, index) => {
      const at = `${where} point ${index}`;
      const errors = [...state.errors];
      if (!Number.isFinite(point.length) || point.length < 0) {
        errors.push(`${at}: length must be a finite number >= 0`);
      }
      if (point.length < state.previous) {
        errors.push(`${at}: lengths must not decrease`);
      }
      if (!Number.isFinite(point.value) || point.value < 0) {
        errors.push(`${at}: value must be a finite number >= 0`);
      }
      if (point.shape === 'curve' && !(typeof point.exponent === 'number' && point.exponent > 0)) {
        errors.push(`${at}: a curve segment needs a positive exponent`);
      }
      return { errors, previous: point.length };
    },
    { errors: [], previous: 0 },
  ).errors;
  return lengthCurve.points.length === 0 ? [`${where}: needs at least one point`] : pointErrors;
}

function sideErrors(side: CurveSide | undefined, name: 'below' | 'above'): string[] {
  if (side === undefined) {
    return [];
  }
  const where = `${name} side`;
  const orderErrors =
    side.min !== undefined &&
    side.max !== undefined &&
    side.min.unit === side.max.unit &&
    side.min.amount > side.max.amount
      ? [`${where}: min must not be above max`]
      : [];
  return [
    ...limitErrors(side.min, `${where} min`),
    ...limitErrors(side.max, `${where} max`),
    ...orderErrors,
    ...(side.edgeByLength ? lengthCurveErrors(side.edgeByLength, `${where} length curve`) : []),
  ];
}

/** Every validity problem with a curve; empty when the curve is valid. */
export function creditCurveErrors(curve: CreditCurve): string[] {
  const [low, high] = curve.plateau;
  const plateauErrors =
    Number.isFinite(low) && Number.isFinite(high) && low <= high
      ? []
      : ['plateau must be two finite numbers with low <= high'];
  const straddleErrors =
    curve.sides !== undefined && !(low <= 0 && high >= 0)
      ? [
          'a curve with per-side settings must have its plateau around the target (low <= 0 <= high)',
        ]
      : [];
  const timeUnitErrors =
    curve.sides !== undefined &&
    [curve.unit, ...sideUnits(curve)].some((unit) => TIME_UNITS.indexOf(unit) < 0)
      ? [`per-side settings are for timing curves (${TIME_UNITS.join(' / ')})`]
      : [];
  return [
    ...plateauErrors,
    ...straddleErrors,
    ...timeUnitErrors,
    ...validateArm(curve.below, 'below'),
    ...validateArm(curve.above, 'above'),
    ...sideErrors(curve.sides?.below, 'below'),
    ...sideErrors(curve.sides?.above, 'above'),
  ];
}

/** The unit each side of a curve is read in. */
export function sideUnits(curve: CreditCurve): readonly [CurveUnit, CurveUnit] {
  return [curve.sides?.below?.unit ?? curve.unit, curve.sides?.above?.unit ?? curve.unit];
}

function lengthPointValue(from: LengthPoint, to: LengthPoint, length: number): number {
  if (to.shape === 'step') {
    return from.value;
  }
  const span = to.length - from.length;
  const progress = span > 0 ? (length - from.length) / span : 1;
  const eased = to.shape === 'curve' ? Math.pow(progress, to.exponent ?? 1) : progress;
  return from.value + (to.value - from.value) * eased;
}

/** The edge's distance from the target (beats) that a length curve gives a note of this length. */
export function lengthCurveDistance(lengthCurve: LengthCurve, lengthBeats: number): number {
  const points = lengthCurve.points;
  const fromTarget = (value: number, length: number): number =>
    lengthCurve.from === 'target' ? value : length - value;
  const first = points[0];
  const last = points[points.length - 1];
  if (lengthBeats <= first.length) {
    return fromTarget(first.value, first.length);
  }
  if (lengthBeats > last.length) {
    return fromTarget(last.value, last.length);
  }
  const segment = points.findIndex((point) => lengthBeats <= point.length);
  return fromTarget(
    lengthPointValue(points[segment - 1], points[segment], lengthBeats),
    lengthBeats,
  );
}

function toMs(amount: number, unit: CurveUnit, scale: NoteScale): number | null {
  if (unit === 'ms') {
    return amount;
  }
  if (unit === 'beats') {
    return scale.msPerBeat === null ? null : amount * scale.msPerBeat;
  }
  if (unit === 'proportion') {
    return scale.lengthMs === null ? null : amount * scale.lengthMs;
  }
  return null;
}

function limitMs(limit: CurveLimit | undefined, scale: NoteScale): number | null | undefined {
  return limit === undefined ? undefined : toMs(limit.amount, limit.unit, scale);
}

function clampDistance(distance: number, min: number | undefined, max: number | undefined): number {
  const floored = min === undefined ? distance : Math.max(distance, min);
  return max === undefined ? floored : Math.min(floored, max);
}

interface ResolvedSide {
  readonly edge: number;
  readonly arm: CurvePoint[];
}

/** One side in ms: the edge's distance from the target, clamped, and the arm beyond it. */
function resolveSide(
  edgeInUnit: number,
  arm: readonly CurvePoint[],
  unit: CurveUnit,
  side: CurveSide | undefined,
  scale: NoteScale,
): ResolvedSide | null {
  const baseEdge = toMs(edgeInUnit, unit, scale);
  const armMs = arm.map((point) => toMs(point.distance, unit, scale));
  const min = limitMs(side?.min, scale);
  const max = limitMs(side?.max, scale);
  const lengthCurve = side?.edgeByLength;
  const lengthShift =
    lengthCurve === undefined
      ? 0
      : scale.lengthBeats === null || scale.msPerBeat === null
        ? null
        : lengthCurveDistance(lengthCurve, scale.lengthBeats) * scale.msPerBeat;
  if (
    baseEdge === null ||
    lengthShift === null ||
    min === null ||
    max === null ||
    armMs.some((distance) => distance === null)
  ) {
    return null;
  }
  const rawEdge = Math.max(0, baseEdge + lengthShift);
  const edge = clampDistance(rawEdge, min, max);
  return {
    edge,
    arm: arm.map((point, index) => ({
      ...point,
      distance: armMs[index] as number,
    })),
  };
}

function needsMs(side: CurveSide | undefined): boolean {
  return (
    side !== undefined &&
    (side.min !== undefined || side.max !== undefined || side.edgeByLength !== undefined)
  );
}

/**
 * The curve as it applies to one note, in a single unit. A curve without per-side settings is
 * returned as it is. When both sides share a unit and carry no limit or length curve, the result
 * is the same curve in that unit. Otherwise every distance is turned into ms for this note: the
 * plateau edges and breakpoints in their side's unit, a length curve's shift added to its edge,
 * then each plateau edge held between its side's floor and cap (the breakpoints keep their widths
 * beyond the edge). Null when the note cannot give what a side needs (no tempo for beats, no
 * length for a proportion).
 */
export function resolveCurve(curve: CreditCurve, scale: NoteScale): CreditCurve | null {
  if (curve.sides === undefined) {
    return curve;
  }
  const [belowUnit, aboveUnit] = sideUnits(curve);
  const { below: belowSide, above: aboveSide } = curve.sides;
  const plain: CreditCurve = {
    unit: belowUnit,
    plateau: curve.plateau,
    below: curve.below,
    above: curve.above,
  };
  if (belowUnit === aboveUnit && !needsMs(belowSide) && !needsMs(aboveSide)) {
    return plain;
  }
  const below = resolveSide(-curve.plateau[0], curve.below, belowUnit, belowSide, scale);
  const above = resolveSide(curve.plateau[1], curve.above, aboveUnit, aboveSide, scale);
  if (below === null || above === null) {
    return null;
  }
  return { unit: 'ms', plateau: [-below.edge, above.edge], below: below.arm, above: above.arm };
}

export function validateCreditCurve(curve: CreditCurve, label = 'credit curve'): void {
  const errors = creditCurveErrors(curve);
  if (errors.length > 0) {
    throw new GradingConfigError(`${label}: ${errors.join('; ')}`);
  }
}

function clampArm(arm: readonly CurvePoint[], maxDistance: number): CurvePoint[] {
  return arm.map((point) => ({ ...point, distance: Math.min(point.distance, maxDistance) }));
}

/**
 * Keeps a window from overrunning the note's own length: the plateau and both arms are cut
 * at `length` from the target on each side. A no-op when the curve already fits.
 */
export function clampCurveToLength(curve: CreditCurve, length: number): CreditCurve {
  const limit = Math.max(0, length);
  const high = Math.min(curve.plateau[1], limit);
  const low = Math.min(Math.max(curve.plateau[0], -limit), high);
  return {
    ...curve,
    plateau: [low, high],
    below: clampArm(curve.below, Math.max(0, limit + low)),
    above: clampArm(curve.above, Math.max(0, limit - high)),
  };
}

/** A curve that gives full credit only at zero deviation: categorical dimensions (pitch, finger). */
export function spikeCurve(unit: CurveUnit): CreditCurve {
  return {
    unit,
    plateau: [0, 0],
    below: [{ distance: 0, credit: 0, shape: 'step' }],
    above: [{ distance: 0, credit: 0, shape: 'step' }],
  };
}

export interface BandedWindowOptions {
  readonly unit: CurveUnit;
  /** Full credit within this distance of the target, early side. */
  readonly fullEarly: number;
  /** Full credit within this distance of the target, late side. */
  readonly fullLate: number;
  /** Partial credit out to this distance, early side. */
  readonly partialEarly: number;
  /** Partial credit out to this distance, late side. */
  readonly partialLate: number;
  /** Credit in the partial band (e.g. 0.7). */
  readonly partialCredit: number;
}

/**
 * The common two-band window: full credit inside, a flat partial credit in the band around it,
 * nothing beyond. Both edges are cliffs.
 */
export function bandedWindowCurve(options: BandedWindowOptions): CreditCurve {
  const arm = (full: number, partial: number): CurvePoint[] => [
    { distance: 0, credit: options.partialCredit, shape: 'step' },
    { distance: Math.max(0, partial - full), credit: 0, shape: 'step' },
  ];
  return {
    unit: options.unit,
    plateau: [-options.fullEarly, options.fullLate],
    below: arm(options.fullEarly, options.partialEarly),
    above: arm(options.fullLate, options.partialLate),
  };
}
